import { Controller } from "@hotwired/stimulus"
import { positionPanel, trackPosition } from "../utils/popover_panel"
import { afterStreamRender } from "../helpers/stream_render"
import Honeybadger from "@honeybadger-io/js"
import { GridSelection } from "../helpers/grid_selection"

// Excel-style cell editing for a configurable list table (grid_table/_table.html.erb).
//
// The model is a SELECTED CELL, not a hovered one: a click selects, editing is a second and
// explicit step (Enter / F2 / double-click / just start typing), and committing moves the
// selection on — Enter down, Tab right. That is what every user of this table already knows from
// Excel, and it is why a click on an editable row no longer opens the record (the name link and a
// double-click on a read-only cell still do).
//
// One controller for the whole table, listening by delegation: a table is 20 columns × 50 rows,
// and a controller per cell would be a thousand of them. Nothing is kept in the DOM that the
// server does not also know — the selection is remembered as {rowId, column} so it survives the
// row being replaced after a save, and every re-render starts from server truth.
//
// Editors are fetched (`data-editor-url`) rather than shipped with every row: 1000 hidden inputs
// per page is what makes a grid like this slow.
const CONTROL = "a, button, input, select, textarea, label"

export default class extends GridSelection(Controller) {
  // `tabSkip: "editable"` makes Tab jump over read-only cells, for a table whose computed columns
  // (a total, a running number) sit between the ones the user types into.
  // `editorUrlTemplate` / `saveUrlTemplate` stand in for a cell's own URLs on a table that would
  // otherwise repeat them in every cell (a position grid: thousands of rows); `__row__` and
  // `__column__` become the row's id and the cell's column.
  static values = {
    errorMessage: String,
    tabSkip: String,
    editorUrlTemplate: String,
    saveUrlTemplate: String,
  }

  connect() {
    this.selected = null
    // Where a run of Tabs started (`{ rowId, column }`). Enter after Tab, Tab returns to that column
    // one row down, as in Excel — typing a row left to right and pressing Enter lands at the start
    // of the next row. Tied to its row, so an origin left over from another row is never used.
    this.tabOrigin = null
    this.editingCell = null
    // The cell whose editor is being fetched, and what has been typed since that started — see
    // openEditor. Without both, fast typing is lost: the first character opens the editor and
    // every character until the response arrives has nowhere to go.
    this.loadingCell = null
    this.typeBuffer = ""
    this.savedHtml = null
    this.initialValue = null
    this.initialChecks = null
    // The saves in flight, keyed by cell — the elements themselves do not survive one, and there
    // can be more than one at a time: clicking a Ja/Nein checkbox while a text cell is open
    // commits both. Keyed rather than a single slot so a second save cannot stand in for the
    // first, and so "is THIS cell saving" stays answerable while another cell's save runs.
    this.saves = new Map()
    this.restorePending = null
    this.untrackPopover = null

    this.onClick = this.ownEvents(this.handleClick)
    this.onClickCapture = this.ownEvents(this.handleClickCapture)
    this.onDoubleClick = this.ownEvents(this.handleDoubleClick)
    this.onKeydown = this.ownEvents(this.handleKeydown)
    this.onKeydownCapture = this.ownEvents(this.handleKeydownCapture)
    this.onFocusOut = this.ownEvents(this.handleFocusOut)
    this.onChange = this.ownEvents(this.handleChange)
    this.onCopy = this.ownEvents(this.handleCopy)
    this.onPaste = this.ownEvents(this.handlePaste)
    this.onSubmit = this.ownEvents(this.handleSubmit)
    this.onStreamRender = this.handleStreamRender.bind(this)

    this.element.addEventListener("click", this.onClick)
    this.element.addEventListener("click", this.onClickCapture, true)
    this.element.addEventListener("dblclick", this.onDoubleClick)
    this.element.addEventListener("keydown", this.onKeydown)
    // On DOCUMENT: a searchable SlimSelect puts focus in its search box, which it mounts in
    // <body>, so a key pressed there never passes through the table at all.
    document.addEventListener("keydown", this.onKeydownCapture, true)
    this.element.addEventListener("focusout", this.onFocusOut)
    this.element.addEventListener("change", this.onChange)
    this.element.addEventListener("copy", this.onCopy)
    this.element.addEventListener("paste", this.onPaste)
    this.element.addEventListener("submit", this.onSubmit)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("click", this.onClickCapture, true)
    this.element.removeEventListener("dblclick", this.onDoubleClick)
    this.element.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("keydown", this.onKeydownCapture, true)
    this.element.removeEventListener("focusout", this.onFocusOut)
    this.element.removeEventListener("change", this.onChange)
    this.element.removeEventListener("copy", this.onCopy)
    this.element.removeEventListener("paste", this.onPaste)
    this.element.removeEventListener("submit", this.onSubmit)
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
    this.untrackPopover?.()
  }

  // A table nested in one of this table's rows (an expense's Belegpositionen under its row in the
  // Spesenzentrale) has a grid controller of its own; its events bubble through this element too,
  // and both answering them would select, open and save every cell twice.
  ownEvents(handler) {
    return (event) => {
      const target = event.target
      const nested =
        target instanceof Element &&
        this.element.contains(target) &&
        target.closest('[data-controller~="grid-cells"]') !== this.element
      if (!nested) handler.call(this, event)
    }
  }

  // -- pointer ----------------------------------------------------------------

  handleClick(event) {
    const cell = event.target.closest("td[data-column]")
    if (!cell || !cell.closest("tr[data-grid-row]")) return

    // A click that lands on a real control (the name link, the checkbox of a Ja/Nein cell, an
    // open editor's input) keeps doing what that control does — the selection just follows it.
    if (cell !== this.editingCell && event.target.closest(CONTROL)) {
      this.tabOrigin = null
      this.select(cell, { focus: false })
      return
    }
    if (cell === this.editingCell) return

    this.tabOrigin = null
    this.commitOpenEditor()
    this.select(cell)
  }

  // A control marked `data-grid-select-first` acts only in a cell that is already selected: the
  // first click selects the cell, as in Excel, and the second does what the control does. For
  // controls that fill most of their cell — a Stundenzettel Matrix entry chip — which would
  // otherwise leave the cell selectable only at its edge. Captured, so the control's own
  // listeners never see the first click. Mouse clicks only: Enter or Space on a focused control
  // arrives as a click with `detail` 0, and holding that back would leave keyboard users with
  // no way to reach the control at all.
  handleClickCapture(event) {
    if (event.detail === 0) return

    const control = event.target.closest("[data-grid-select-first]")
    const cell = control?.closest("td[data-column]")
    if (!cell || !this.element.contains(cell) || cell.hasAttribute("data-selected")) return

    event.preventDefault()
    event.stopPropagation()
    this.tabOrigin = null
    this.commitOpenEditor()
    this.select(cell)
  }

  // Excel opens a cell for editing on double-click. A cell that cannot be edited has no second
  // meaning for the gesture, so it falls back to the row's own job: opening the record — through
  // the row's first link, which is its name link. A row with no record link of its own says so
  // (`data-grid-no-record-link`), or the fallback would click whatever link the row happens to
  // carry (a hint, a lightbox).
  //
  // A cell can name its own double-click target instead (`data-grid-cell-open`): an empty
  // Stundenzettel Matrix cell opens the full new-entry form that way, since typing is what
  // its editor is for.
  handleDoubleClick(event) {
    const cell = event.target.closest("td[data-column]")
    if (!cell || !cell.closest("tr[data-grid-row]")) return
    if (event.target.closest(CONTROL)) return

    const opener = cell.querySelector("[data-grid-cell-open]")
    if (opener) {
      opener.click()
    } else if (cell.hasAttribute("data-editable")) {
      this.select(cell, { focus: false })
      this.openEditor(cell)
    } else if (!cell.closest("tr").hasAttribute("data-grid-no-record-link")) {
      cell.closest("tr").querySelector("a[href]")?.click()
    }
  }

  // -- keyboard ---------------------------------------------------------------

  handleKeydown(event) {
    if (this.editingCell) {
      this.editingKeydown(event)
      return
    }
    if (this.loadingCell) {
      this.loadingKeydown(event)
      return
    }
    if (!this.selected) return

    const cell = this.findCell(this.selected)
    if (!cell) return
    if (this.claimedAsCommand(event, cell)) return

    if (this.navigationKey(event, cell)) return

    if (event.key === "Enter" || event.key === "F2") {
      event.preventDefault()
      this.openEditor(cell)
    } else if (event.key === "Escape") {
      this.tabOrigin = null
      this.clearSelection()
    } else if (event.key === " " && cell.querySelector("input[type=checkbox]")) {
      // Space ticks a Ja/Nein cell. On any other cell it is just a character, and falls through
      // to the type-over branch below — where preventDefault also stops the page from scrolling.
      this.toggleCheckbox(event, cell)
    } else if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault()
      this.clearCell(cell)
    } else if (this.printable(event)) {
      event.preventDefault()
      this.openEditor(cell, { typed: event.key })
    }
  }

  // Typing continues while the editor is still being fetched. Those characters are collected
  // here and replayed into the input the moment it arrives, so a fast typist never loses the
  // middle of what they typed.
  loadingKeydown(event) {
    if (this.printable(event)) {
      event.preventDefault()
      this.typeBuffer += event.key
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.loadingCell = null
    } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      // Strg+Enter before the editor arrived: commit in place on arrival, then the command —
      // typing "250" and Strg+Enter in one go is exactly the fast entry it exists for.
      event.preventDefault()
      this.pendingCommand = event
    } else if (event.key === "Enter" || event.key === "Tab") {
      // Committing before there is anything to commit: hold the intent and apply it on arrival.
      event.preventDefault()
      if (event.key === "Tab") this.startTabRun()
      this.pendingCommit =
        event.key === "Enter" ? this.enterMove(event.shiftKey) : this.tabMove(event.shiftKey)
    }
  }

  // Keys the grid has no meaning for can be claimed by a controller on the same table: a tree
  // list adds rows, indents and collapses groups from the keyboard. The table dispatches
  // `grid-cells:command` before handling a key on a selected cell; a listener that calls
  // preventDefault() on it owns the key, and the grid then leaves it alone. Asked first, so the
  // listener can also take combinations the grid would otherwise read — Alt+Shift+Arrow is a move
  // here, but an indent in a tree.
  claimedAsCommand(event, cell) {
    const command = this.dispatch("command", {
      cancelable: true,
      detail: {
        originalEvent: event,
        rowId: cell.closest("tr").dataset.rowId,
        column: cell.dataset.column,
      },
    })
    if (!command.defaultPrevented) return false

    event.preventDefault()
    this.tabOrigin = null
    return true
  }

  // A select's own control swallows both Enter and Escape — SlimSelect applies the highlighted
  // option, or closes its list, and stops the event before the table's own handler sees it. With
  // the search box open the key does not even land inside the table, since SlimSelect mounts that
  // box in <body>.
  // Captured here instead, because either key means the user is done with this cell, and leaving
  // the editor open would strand them: the arrow keys then go to a closed dropdown instead of
  // moving the selection, and nothing short of a real commit gets the grid back.
  //
  // Queued rather than run inline, so the control has its turn first: if it started a save, that
  // save closes the editor by replacing the row, and this leaves it alone.
  handleKeydownCapture(event) {
    if (event.key !== "Enter" && event.key !== "Escape") return
    if (!this.editingCell || this.commitMode() !== "change") return

    setTimeout(() => {
      if (this.editingCell && !this.saveInFlight(this.editingCell))
        this.cancelEditor({ reselect: true })
    }, 0)
  }

  editingKeydown(event) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      this.commitThenCommand(event)
      return
    }
    if (event.key === "Enter") {
      // A cell that commits on change never commits from here. Its control owns Enter: SlimSelect
      // applies the highlighted option and fires `change`, and THAT is the commit. Committing here
      // too would send the same cell twice, and the later response — rendered last — would decide
      // what the row ends up showing.
      if (this.commitMode() === "change") {
        // The list still open, or this cell's save already on its way: the control is mid-gesture.
        if (this.listConsumingKeys() || this.saveInFlight(this.editingCell)) return

        // Nothing was picked and nothing is in flight — Enter just means "done here".
        event.preventDefault()
        this.cancelEditor({ reselect: true })
        return
      }

      // The date field handles Enter first (it commits the typed date into its hidden ISO field
      // and closes the calendar) and prevents the default; the value is settled by the time this
      // runs, so Enter still means "commit and move down".
      event.preventDefault()
      this.commit(this.enterMove(event.shiftKey))
    } else if (event.key === "Tab") {
      event.preventDefault()
      this.startTabRun()
      this.commit(this.tabMove(event.shiftKey))
    } else if (event.key === "Escape") {
      // A date picker or a select swallows the first Escape to close its own dropdown; only the
      // one that reaches here untouched cancels the edit.
      if (event.defaultPrevented) return
      event.preventDefault()
      this.cancelEditor({ reselect: true })
    }
  }

  // Strg/Cmd+Enter while typing means "done here, and on to the next row": the cell commits where
  // it is, and the key goes on to whoever claims it as a command (a tree list inserts a row).
  // The command waits for the save — a row drafted under a value the server then rejects would
  // pull focus away from the editor that comes back carrying the message. Unclaimed, it is Enter.
  commitThenCommand(event) {
    const cell = this.editingCell
    if (!cell) return

    const id = this.identify(cell)
    this.commit()
    if (this.saveInFlight(cell)) this.commandAfterSave = { event, id }
    else this.runCommand(event, id)
  }

  runCommand(event, id) {
    const cell = this.findCell(id)
    if (!cell || this.claimedAsCommand(event, cell)) return

    this.moveSelection(...this.enterMove(event.shiftKey))
  }

  // The command waiting for exactly this save, taken so it runs at most once.
  takeCommandAfterSave({ rowId, column }) {
    const command = this.commandAfterSave
    if (command?.id.rowId !== rowId || command.id.column !== column) return null

    this.commandAfterSave = null
    return command
  }

  commitMode() {
    return this.editingCell?.querySelector("[data-cell-editor]")?.dataset.commit
  }

  // True while the cell's own dropdown list is open and owning the keyboard. Only one such list
  // can be open at a time, so finding any open one is enough.
  listConsumingKeys() {
    return !!document.querySelector(".ss-content.ss-open")
  }

  toggleCheckbox(event, cell) {
    const checkbox = cell.querySelector("input[type=checkbox]")
    if (!checkbox) return

    event.preventDefault()
    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // -- clipboard --------------------------------------------------------------

  handleCopy(event) {
    if (this.editingCell || !this.selected) return

    const cell = this.findCell(this.selected)
    if (!cell) return

    // A cell whose text is decorated or rounded for display (a currency, a badge) names the value
    // it copies on an element inside it, so a copied cell pastes back as what it holds.
    const source = cell.querySelector("[data-copy-value]")
    event.preventDefault()
    event.clipboardData.setData(
      "text/plain",
      source ? source.dataset.copyValue : cell.innerText.trim(),
    )
  }

  // Reads from the paste event rather than the clipboard API, which would need a permission
  // prompt for something the user has already explicitly asked for.
  handlePaste(event) {
    if (this.editingCell || !this.selected) return

    const cell = this.findCell(this.selected)
    if (!cell || !this.directlyWritable(cell)) return

    event.preventDefault()
    this.writeValue(cell, event.clipboardData.getData("text/plain").trim())
  }

  // The two gestures that write without opening an editor: Delete clears a cell, a paste
  // overwrites it. Neither means anything for the popover cells — "empty" is not a weekday set,
  // and a pasted string is not an effort with a unit — nor for a Ja/Nein cell: its checkbox has
  // no text to copy, so copying one yields "", and ActiveModel's boolean cast reads everything
  // that is not "0"/"false"/"off"/"" as TRUE, so pasting the word "Nein" would tick it.
  directlyWritable(cell) {
    if (!cell.hasAttribute("data-editable")) return false

    // The server stamps which cells open a card instead of an input (`data-editor-popover`,
    // GridTable::EditableCell#popover?), so the list of those editors lives in one place.
    return !cell.hasAttribute("data-editor-popover") && cell.dataset.editor !== "checkbox"
  }

  // -- editor -----------------------------------------------------------------

  // `typed` is the character that started a type-over, or null when the editor was opened with
  // Enter/F2/double-click and should therefore open on the current value.
  //
  // A second call for a cell already loading is ignored: every keystroke would otherwise start
  // its own fetch, and the last response to arrive would win with only its own character in it.
  async openEditor(cell, { typed = null } = {}) {
    if (!cell.hasAttribute("data-editable")) return
    if (this.editingCell === cell || this.loadingCell === cell) return

    this.commitOpenEditor()

    const url = this.editorUrl(cell)
    if (!url) return

    this.loadingCell = cell
    this.typeBuffer = typed ?? ""
    this.pendingCommit = null
    this.pendingCommand = null
    this.commandAfterSave = null

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      })
      if (!response.ok) throw new Error(`editor responded ${response.status}`)

      const html = await response.text()
      // Escape while the editor was loading, or the cell replaced by a save elsewhere: there is
      // nothing left to open.
      if (this.loadingCell !== cell) return

      const target = this.findCell({
        rowId: cell.closest("tr").dataset.rowId,
        column: cell.dataset.column,
      })
      if (!target) return

      this.savedHtml = target.innerHTML
      target.innerHTML = html
      target.setAttribute("data-editing", "")
      this.editingCell = target
      this.activateEditor(target, typed === null ? null : this.typeBuffer)

      // Enter, Tab or Strg+Enter pressed before the editor had arrived still means what it says.
      if (this.pendingCommand) {
        const command = this.pendingCommand
        this.pendingCommand = null
        this.commitThenCommand(command)
      } else if (this.pendingCommit) {
        const move = this.pendingCommit
        this.pendingCommit = null
        this.commit(move)
      }
    } catch (error) {
      Honeybadger.notify(error, { context: { url } })
      this.cancelEditor()
    } finally {
      if (this.loadingCell === cell) this.loadingCell = null
    }
  }

  // Opens the cell's editor card, if its editor is one. Also called when a rejected save brings
  // the card back with its message: the stream renders it closed, and a closed card would leave
  // the cell in edit mode with nothing on screen to correct.
  //
  // `[data-cell-popover]`, not `[popover]`: the date editor ships the date picker's own calendar
  // panel, which is a popover too. Opening that as if it were the editor card put the calendar
  // on screen unasked, and the toggle handler below then read the first Escape or Enter — which
  // merely closes the calendar — as "the edit was abandoned", so no date could ever be saved.
  showCard(cell) {
    // Already open: the same card, toggle handler and all — the stream left the editor in place.
    const popover = cell.querySelector("[data-cell-popover]")
    if (!popover || popover.matches(":popover-open")) return

    popover.showPopover()
    const reposition = () => positionPanel(cell, popover)
    reposition()
    this.untrackPopover?.()
    this.untrackPopover = trackPosition(reposition)
    popover.addEventListener("toggle", (event) => {
      if (event.newState !== "closed" || this.editingCell !== cell) return

      // Light dismiss (a click outside) closes the card. That abandons the edit, except on a
      // card that commits on blur (the task end date's): there it is leaving the cell, and saves
      // what was typed. Escape never gets here — editingKeydown has cancelled by then.
      if (this.commitMode() === "blur") this.commit()
      else this.cancelEditor({ reselect: true })
    })
  }

  activateEditor(cell, typedValue) {
    // `[data-cell-popover]`, not `[popover]`: the date editor ships the date picker's own calendar
    // panel, which is a popover too. Opening that as if it were the editor card put the calendar
    // on screen unasked, and the toggle handler below then read the first Escape or Enter — which
    // merely closes the calendar — as "the edit was abandoned", so no date could ever be saved.
    this.showCard(cell)

    const input = cell.querySelector("input:not([type=hidden]), select, textarea")
    if (!input) return

    // Captured BEFORE a type-over overwrites it: this is what "has the value changed" compares
    // against. An input's own defaultValue cannot be trusted — the date field rewrites its
    // hidden companion's value attribute as you type, so by commit time it equals what was typed
    // and every date edit looks unchanged.
    this.initialValue = this.valueOf(cell)
    this.initialChecks = this.checksOf(cell)

    if (input.tagName === "SELECT") {
      this.activateSelectEditor(cell)
      return
    }

    // Type-over replaces the value, as in Excel; opening with Enter/F2 keeps it and selects it,
    // so the next keystroke can either replace it or be arrowed away from.
    if (typedValue !== null && "value" in input) input.value = typedValue
    input.focus()
    if (typedValue === null && input.select) input.select()
    else if (input.setSelectionRange && input.type === "text") {
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }

  // SlimSelect builds its own control and leaves the original <select> in the cell, visually
  // hidden but still focusable. Focusing that original — which is what focusing "the input" does
  // — is why the browser's own dropdown appeared on the arrow keys while the list on screen never
  // moved, and why Enter then committed the original's value (often the empty one) instead of the
  // highlighted option. Focus SlimSelect's control instead and open it: the whole edit on this
  // cell is picking from a list, so the list is what should appear.
  //
  // With focus there, SlimSelect owns the keyboard: arrows move its highlight, Enter applies it
  // and fires `change` (which commits the cell), and Escape closes the list and stops there — so
  // the second Escape is the one that cancels the edit.
  async activateSelectEditor(cell) {
    const main = await this.waitForElement(() => cell.querySelector(".ss-main"))
    if (!main || this.editingCell !== cell) return

    main.focus()
    main.click()
  }

  // SlimSelect is built when Stimulus connects the injected editor, a moment after the HTML lands,
  // so the control cannot simply be queried for straight away.
  //
  // A timer rather than requestAnimationFrame: rAF only runs while the tab is actually being
  // painted, and a callback that never fires would leave the cell in edit mode with nothing
  // focused. A timer fires either way.
  waitForElement(find, { attempts = 40, intervalMs = 20 } = {}) {
    return new Promise((resolve) => {
      const tick = (left) => {
        const found = find()
        if (found || left === 0) return resolve(found ?? null)

        setTimeout(() => tick(left - 1), intervalMs)
      }
      tick(attempts)
    })
  }

  // Restores the display cell. `reselect` puts the selection frame back on it, which is what
  // Escape should leave behind; a cancel caused by the row being replaced must not.
  cancelEditor({ reselect = false } = {}) {
    const cell = this.editingCell
    this.editingCell = null
    this.loadingCell = null
    this.pendingCommit = null
    this.pendingCommand = null
    this.commandAfterSave = null
    this.restorePending = null
    this.untrackPopover?.()
    this.untrackPopover = null
    if (!cell) return

    cell.querySelector("[popover]:popover-open")?.hidePopover()
    if (this.savedHtml !== null) cell.innerHTML = this.savedHtml
    this.savedHtml = null
    this.initialValue = null
    this.initialChecks = null
    cell.removeAttribute("data-editing")
    if (reselect) this.select(cell)
  }

  // Cancel from the popover card's Abbrechen button.
  cancel(event) {
    event.preventDefault()
    this.cancelEditor({ reselect: true })
  }

  // -- committing -------------------------------------------------------------

  // `move` is where the selection goes once the server answers; the row is replaced by then, so
  // it cannot be applied here.
  commit(move = null) {
    const cell = this.editingCell
    if (!cell || cell.hasAttribute("data-saving")) return

    const form = cell.querySelector("form")
    if (!form) return

    if (!this.changed(cell)) {
      this.cancelEditor({ reselect: true })
      if (move) this.moveSelection(...move)
      return
    }

    this.sendSave(cell, new FormData(form), { move })
  }

  // Committing whatever is open because the user moved on (clicked another cell, left the table).
  // Only a cell that commits on blur has something to save at this point: a popover card has its
  // own Speichern button, and a select or checkbox has already saved the moment it was picked —
  // walking away from either is a cancel, not a save.
  commitOpenEditor() {
    if (!this.editingCell) return

    if (this.commitMode() === "blur") this.commit()
    else this.cancelEditor()
  }

  // What this cell would submit right now. The date field's typed text is mirrored into a hidden
  // ISO companion, and that companion is what gets sent — so it, not the visible text, is the
  // value that counts.
  valueOf(cell) {
    const hidden = cell.querySelector("input[type=hidden][name='cell[value]']")
    if (hidden) return hidden.value

    return cell.querySelector("input:not([type=hidden]), select, textarea")?.value ?? ""
  }

  // An editor can say it changed although its text did not — a pick from a list whose name is the
  // text already there (the Kalkulation row's Bezeichnung, component-picker) — by marking an
  // element `data-cell-changed`.
  changed(cell) {
    const input = cell.querySelector("input:not([type=hidden]), select, textarea")
    if (!input) return true
    if (input.type === "checkbox") return true
    if (this.initialValue === null) return true
    if (cell.querySelector("[data-cell-changed]")) return true
    // A checkbox beside the value (the end date's "Offenes Ende") is a change of its own.
    if (this.initialChecks !== null && this.checksOf(cell) !== this.initialChecks) return true

    return this.valueOf(cell) !== this.initialValue
  }

  checksOf(cell) {
    return [...cell.querySelectorAll("input[type=checkbox]")].map((box) => box.checked).join()
  }

  // Blur commit: leaving a cell saves what was typed, as it does in a spreadsheet. Focus moving
  // into the cell's own calendar or SlimSelect dropdown is not leaving it — both render outside
  // the cell, in the top layer or at the end of <body>.
  handleFocusOut(event) {
    if (!this.editingCell) return

    if (this.commitMode() !== "blur") return

    const next = event.relatedTarget
    if (next && (this.editingCell.contains(next) || next.closest("[popover], .ss-content"))) return

    // The blur may be the browser moving focus during the row replacement that a commit already
    // started; a queued task lets that settle first.
    setTimeout(() => {
      if (!this.editingCell || this.editingCell.contains(document.activeElement)) return
      if (document.activeElement?.closest("[popover], .ss-content")) return

      this.commit()
    }, 0)
  }

  // A checkbox or a select is committed the moment its value is picked — there is nothing else
  // to type into it.
  handleChange(event) {
    const cell = event.target.closest("td[data-column]")
    const editor = cell?.querySelector("[data-cell-editor]")
    if (!editor || editor.dataset.commit !== "change") return
    if (!cell.closest("tr[data-grid-row]")) return

    const form = event.target.closest("form")
    if (!form) return

    this.select(cell, { focus: false })
    this.sendSave(cell, new FormData(form))
  }

  // The popover cards' own Speichern button submits natively; everything else reaches sendSave
  // directly. Either way the submission must not go on to Turbo — see sendSave.
  handleSubmit(event) {
    const form = event.target
    if (!form.matches(".grid-cell-editor-form")) return

    event.preventDefault()
    const cell = form.closest("td[data-column]")
    if (cell) this.sendSave(cell, new FormData(form))
  }

  // Every cell save is fetched here rather than handed to Turbo as a form submission, because a
  // turbo-frame keeps exactly ONE submission and stops the previous one when a second starts
  // (FrameController#formSubmitted). The whole table lives in the list's turbo frame, so
  // ticking a Ja/Nein box while a text cell was open cancelled the text cell's save: the write
  // had already reached the server, but its response was thrown away — the row never refreshed
  // and the cell stayed marked as saving, which made commit() refuse it from then on. Fetching
  // each save ourselves makes two of them genuinely independent.
  async sendSave(cell, body, { move = null } = {}) {
    const id = this.identify(cell)
    const key = this.cellKey(id)
    const url = this.saveUrl(cell)
    if (!url) return

    this.saves.set(key, id)
    cell.setAttribute("data-saving", "")

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
        },
        body,
      })
      const html = await response.text()
      this.saves.delete(key)
      // Every answer the endpoint means to give is a stream — a rejection included. Anything else
      // (the bare 404 of a column that is not editable, a 500 error page) renders no stream, so
      // the restore below would never be consumed: the cell would stay marked as saving and the
      // NEXT cell's stream would pick the stale restore up and move the selection to this one.
      if (!this.streamResponse(response)) {
        this.saveFailed(id)
        return
      }
      // Hand the restore to the stream hook rather than doing it here: the stream render is
      // asynchronous, so re-selecting straight after this call would style the row that is about
      // to be thrown away, and the selection would vanish with it. `turbo:before-stream-render`
      // fires synchronously from renderStreamMessage, so this slot is always consumed by the
      // message it was set for.
      this.restorePending = { committing: id, move, success: response.ok }
      window.Turbo.renderStreamMessage(html)
    } catch (error) {
      this.saves.delete(key)
      this.saveFailed(id)
      Honeybadger.notify(error, { context: { url } })
    }
  }

  streamResponse(response) {
    return (response.headers.get("Content-Type") || "").includes("text/vnd.turbo-stream.html")
  }

  // Nothing came back to render, so the cell is restored exactly like a rejected save — its open
  // editor stays open, otherwise the selection returns — and the reason goes into a toast.
  saveFailed(id) {
    this.applyRestore({ committing: id, move: null, success: false })
    this.showErrorToast()
  }

  // Same toast as the column layout's (grid_columns_controller) — the flash container is
  // server-rendered, and there is no server answer here to carry one.
  showErrorToast() {
    if (!this.errorMessageValue) return

    const toast = document.createElement("div")
    toast.className =
      "fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg"
    toast.setAttribute("role", "status")
    toast.textContent = this.errorMessageValue
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }

  // The row names both URLs of a cell: `data-editor-url` is the GET that opens its editor,
  // `data-save-url` the cell itself. Neither is derived from the other here — that would tie
  // every list to one route shape. A table may name them once, as templates, instead.
  editorUrl(cell) {
    return cell.dataset.editorUrl ?? this.fromTemplate(this.editorUrlTemplateValue, cell)
  }

  saveUrl(cell) {
    return cell.dataset.saveUrl ?? this.fromTemplate(this.saveUrlTemplateValue, cell)
  }

  // The column keeps its `:` (a Zusatzfeld's `cf:<key>`), as Rails' own path helpers leave it: a
  // path segment may carry one, and the cells routes match their constraint against the path as
  // sent — `cf%3Afarbe` matches no route.
  fromTemplate(template, cell) {
    const rowId = cell.closest("[data-row-id]")?.dataset.rowId
    if (!template || !rowId) return null

    return template
      .replace("__row__", encodeURIComponent(rowId))
      .replace("__column__", encodeURIComponent(cell.dataset.column).replaceAll("%3A", ":"))
  }

  identify(cell) {
    return { rowId: cell.closest("tr").dataset.rowId, column: cell.dataset.column }
  }

  cellKey({ rowId, column }) {
    return `${rowId}/${column}`
  }

  saveInFlight(cell) {
    return !!cell && this.saves.has(this.cellKey(this.identify(cell)))
  }

  // Turbo renders a stream asynchronously, so neither this event nor a following animation frame
  // is late enough — both still see the old row, and a selection put back there is thrown away
  // by the swap a moment later. Wrapping `detail.render` is the documented hook that runs after
  // the element has actually been replaced (see helpers/stream_render).
  //
  // A save may answer with more rows than the saved one (a position grid streams the rows its sums
  // moved), and the selection the first stream put back — or moved on with Enter — can sit in one
  // of those. Every later stream therefore keeps the selection: a row it replaced comes back
  // without the frame, and the keys would go nowhere.
  handleStreamRender(event) {
    if (!this.restorePending) {
      if (this.selected) afterStreamRender(event, () => this.keepSelection())
      return
    }

    const pending = this.restorePending
    this.restorePending = null
    afterStreamRender(event, () => this.applyRestore(pending))
  }

  keepSelection() {
    if (this.editingCell || this.loadingCell || this.element.querySelector("td[data-selected]"))
      return

    const cell = this.findCell(this.selected)
    if (cell) this.select(cell, { focus: this.focusLost() })
  }

  // The cell is re-found by id: whichever of the row or the editor the stream replaced, every
  // element held across the request is a different one now.
  applyRestore({ committing, move, success }) {
    const cell = this.findCell(committing)
    const command = this.takeCommandAfterSave(committing)

    if (!success) {
      cell?.removeAttribute("data-saving")
      // `data-editing` is the honest test for "an editor is open here", and only openEditor sets
      // it. A Ja/Nein tick, a Delete and a paste all write without one — calling their display
      // cell the open editor would route every later keydown through editingKeydown, where a cell
      // with no form commits nothing and the arrow keys stop moving, leaving the grid keyboard-
      // dead until the user happens to press Escape.
      if (cell?.hasAttribute("data-editing")) {
        // The rejected save replaced the editor with one carrying the message; it stays open.
        this.editingCell = cell
        this.showCard(cell)
        cell.querySelector("input:not([type=hidden]), select, textarea")?.focus()
      } else if (!this.editingCell && !this.loadingCell) {
        // The row came back unchanged and the message is in the flash. Put the selection back on
        // the cell, unless the user has meanwhile opened an editor somewhere else.
        this.select(cell, { focus: this.focusLost() })
      }
      return
    }

    this.closeSavedEditor(committing)

    // Clicking another cell while one is being edited commits the edit AND selects the clicked
    // cell — so when the selection has already moved on by the time the save lands, it wins.
    // Putting the frame back on the saved cell would drag the user back where they left.
    if (this.movedOn(committing)) {
      this.select(this.findCell(this.selected), { focus: this.focusLost() })
      return
    }

    this.selected = committing
    this.select(cell, { focus: this.focusLost() })
    if (move) this.moveSelection(...move)
    if (command) this.runCommand(command.event, committing)
  }

  // A save replaces the whole <tr>, so the editor that was saved is gone either way — but an
  // editor opened in ANOTHER row while this save was in flight (clicking a second cell commits
  // the first and opens that one) is still on screen. Closing it from here would strand its
  // input: nothing would commit it, and Escape could not restore the cell around it because
  // `savedHtml` would be gone.
  closeSavedEditor(committing) {
    const open = this.editingCell
    if (open?.isConnected && !this.sameCell(open, committing)) return

    this.editingCell = null
    this.savedHtml = null
    this.initialValue = null
    this.initialChecks = null
    this.untrackPopover?.()
    this.untrackPopover = null
  }

  sameCell(cell, { rowId, column }) {
    return cell.closest("tr")?.dataset.rowId === String(rowId) && cell.dataset.column === column
  }

  // Whether focus has nowhere left to be. Replacing the row detaches the element that had it, and
  // the browser drops focus on <body> — where the keydown listener, which sits on the scroller,
  // never sees it again, so the grid goes deaf until the next click. Focus is only ever taken
  // back in that case: if the user has moved it on themselves — to the search box, to another
  // cell, into an editor still open in a row this save did not touch — it stays where they put it.
  focusLost() {
    const active = document.activeElement
    return !active || active === document.body
  }

  movedOn({ rowId, column }) {
    return this.selected && (this.selected.rowId !== rowId || this.selected.column !== column)
  }

  // A value written without opening the editor: Delete clears the cell, a paste overwrites it.
  // Which cells that is allowed for is directlyWritable().
  writeValue(cell, value) {
    const body = new FormData()
    body.append("cell[value]", value)
    // No editor was opened for this write, so a rejection cannot be answered by replacing one:
    // the server sends back the row plus a flash instead. Without this the 422 would target an
    // element that is not in the document, Turbo would drop it, and the user would see the value
    // simply not change with nothing saying why.
    body.append("cell[without_editor]", "1")

    this.sendSave(cell, body)
  }

  clearCell(cell) {
    if (!this.directlyWritable(cell)) return

    this.writeValue(cell, "")
  }
}
